import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { Play } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

const VideoSection = memo(() => {
  const [videoData, setVideoData] = useState<any[]>([]);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const { data } = await supabase
          .from("video_of_the_day")
          .select("youtube_url")
          .eq("is_active", true)
          .order("created_at", { ascending: false })
          .limit(1);
        if (data) setVideoData(data);
      } catch (error) {
        console.error("Error fetching video:", error);
      }
    };
    fetchVideo();
  }, []);

  const currentVideoId = useMemo(() => {
    if (videoData?.length > 0) {
      const url = videoData[0].youtube_url;
      if (url.includes("youtube.com/watch?v=")) return url.split("v=")[1]?.split("&")[0];
      if (url.includes("youtu.be/")) return url.split("youtu.be/")[1]?.split("?")[0];
      if (url.includes("youtube.com/embed/")) return url.split("embed/")[1]?.split("?")[0];
    }
    return "BGFV1owhJfQ";
  }, [videoData]);

  const handleVideoClick = useCallback(() => {
    window.open(`https://www.youtube.com/watch?v=${currentVideoId}`, "_blank");
  }, [currentVideoId]);

  return (
    <div className="flex flex-col items-center">
      {/* Device frame like reference */}
      <motion.div
        onClick={handleVideoClick}
        className="relative cursor-pointer group"
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300 }}
        aria-label="Play educational video for kids"
      >
        {/* Yellow border glow */}
        <div className="absolute -inset-3 bg-yellow-400 rounded-[2rem] shadow-xl" />
        
        {/* Device body */}
        <div className="relative bg-gray-900 rounded-[1.5rem] p-2 shadow-2xl">
          {/* Screen */}
          <div className="relative rounded-[1rem] overflow-hidden w-[320px] h-[230px] sm:w-[380px] sm:h-[270px] md:w-[460px] md:h-[320px] lg:w-[520px] lg:h-[360px]">
            <img
              src={`https://img.youtube.com/vi/${currentVideoId}/hqdefault.jpg`}
              alt="Video of the day - Educational content for kids"
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
              width={480}
              height={360}
            />
            {/* Play overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-all">
              <motion.div
                className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-2xl border-4 border-white/40"
                whileHover={{ scale: 1.15 }}
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Play className="text-white ml-1" size={36} fill="currentColor" />
              </motion.div>
            </div>
          </div>
        </div>

        {/* Floating mascots around the frame */}
        <motion.div
          className="absolute -top-6 -right-6 text-3xl"
          animate={{ y: [0, -6, 0], rotate: [0, 10, 0] }}
          transition={{ duration: 2.5, repeat: Infinity }}
        >
          🦁
        </motion.div>
        <motion.div
          className="absolute -bottom-5 -left-5 text-3xl"
          animate={{ y: [0, -5, 0], rotate: [0, -8, 0] }}
          transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
        >
          🐸
        </motion.div>
      </motion.div>

      <p className="mt-6 text-base md:text-lg font-fredoka font-bold text-cyan-800 text-center">
        🎬 Video of the Day
      </p>
    </div>
  );
});

VideoSection.displayName = "VideoSection";

export default VideoSection;
